import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sprout } from 'lucide-react';
import { NAV_ITEMS } from '../constants';

interface NavbarProps {
  isScrolled: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ isScrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();

  const handleNavClick = () => {
    setIsOpen(false);
  };

  // Determine navbar style based on scroll or current page
  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;

  // Split nav items for desktop layout
  const leftNavItems = NAV_ITEMS.slice(0, 2); // Home, About
  const rightNavItems = NAV_ITEMS.slice(2);   // Products, Careers, Contact

  const renderNavLink = (item: typeof NAV_ITEMS[0]) => {
    const isActive = location.pathname === item.path;

    const activeClass = isTransparent ? 'text-white' : 'text-gray-900';
    const inactiveClass = isTransparent ? 'text-white/80 hover:text-avo-gold' : 'text-gray-600 hover:text-avo-main';

    return (
        <Link 
            key={item.id}
            to={item.path || '/'}
            onClick={handleNavClick}
            className={`
                font-heading text-sm font-bold tracking-widest uppercase transition-all flex items-center gap-1 py-2
                ${isActive ? activeClass : inactiveClass}
            `}
        >
            {item.label}
        </Link>
    );
  };

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-500 ${
        isTransparent ? 'bg-gradient-to-b from-black/60 to-transparent py-6' : 'bg-white shadow-md py-2'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center relative">
            
            {/* Desktop Left Nav */}
            <div className="hidden md:flex flex-1 justify-end items-center space-x-6 lg:space-x-8 pr-6 lg:pr-12">
                {leftNavItems.map(item => renderNavLink(item))}
            </div>

            {/* Center Logo */}
            <Link 
                to="/"
                className="flex-shrink-0 flex items-center justify-center cursor-pointer z-10 mx-4" 
                onClick={handleNavClick}
            >
                <div className="flex flex-col items-center text-center group">
                    {!logoError ? (
                        <img 
                            src="https://lh3.googleusercontent.com/d/1nElyiFHM8y4hVlChTDXZZ2an5ihGB10B" 
                            alt="Villa Avoils Logo" 
                            className={`w-auto object-contain transition-all duration-500 group-hover:scale-105 ${
                                isTransparent ? 'h-16 md:h-20 mb-1' : 'h-10 md:h-12 mb-0.5'
                            }`}
                            onError={() => setLogoError(true)}
                        />
                    ) : (
                        <div className={`transition-all duration-500 rounded-full border-2 flex items-center justify-center ${
                             isTransparent ? 'border-white bg-white/10 p-3 mb-1' : 'border-avo-main bg-avo-main/5 p-2 mb-0.5'
                        }`}>
                            <Sprout 
                                size={isTransparent ? 32 : 20} 
                                className={`transition-all duration-500 ${isTransparent ? 'text-white' : 'text-avo-main'}`} 
                            />
                        </div>
                    )}
                    
                    <span className={`font-heading font-bold tracking-widest uppercase transition-all duration-500 ${
                        isTransparent ? 'text-white text-sm' : 'text-avo-forest text-xs'
                    }`}>
                        VILLA AVOILS
                    </span>
                    <span className={`uppercase tracking-[0.2em] transition-all duration-500 ${
                        isTransparent ? 'text-white/80 text-[10px]' : 'text-gray-600 text-[8px]'
                    }`}>
                        International
                    </span>
                </div>
            </Link>

            {/* Desktop Right Nav */}
            <div className="hidden md:flex flex-1 justify-start items-center space-x-6 lg:space-x-8 pl-6 lg:pl-12">
                {rightNavItems.map(item => renderNavLink(item))}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden absolute right-0">
                <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2 rounded-md ${isTransparent ? 'text-white' : 'text-gray-800'}`}
                >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-white transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: '0', paddingTop: '100px' }}
      >
        <div className="px-6 py-6 space-y-6 h-full overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <div key={item.id} className="border-b border-stone-100 pb-4">
              <Link
                to={item.path || '/'}
                onClick={handleNavClick}
                className={`block text-xl font-heading font-bold w-full text-left uppercase tracking-wide
                    ${location.pathname === item.path ? 'text-avo-gold' : 'text-gray-900'}`}
              >
                {item.label}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};